import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllCoursesOptions,
	enrollStudentMutation,
	unenrollStudentMutation,
	cancelUnenrollStudentMutation,
	getAllCoursesQueryKey,
	getAllStudentsOptions,
	getAllStudentsQueryKey,
	createAndSendSignupLinkMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
	GraduationCap,
	School,
	UserPlus,
	UserMinus,
	Eye,
	Send,
	Loader2,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
	getSubjectLabel,
	getLevelLabel,
	getSchoolLabel,
	getTimeSlot,
} from "@/stores/enum-store";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterText,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";
import { UnenrollDialog } from "@/components/UnenrollDialog";

export const Route = createFileRoute("/admin/students/")({
	component: StudentsManagementPage,
});

function StudentsManagementPage() {
	const queryClient = useQueryClient();
	const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
	const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
	const [unenrollStudentId, setUnenrollStudentId] = useState("");
	const [unenrollStudentName, setUnenrollStudentName] = useState("");
	const [unenrollCourseId, setUnenrollCourseId] = useState("");
	const [unenrollCourseName, setUnenrollCourseName] = useState("");
	const [existingUnenrollDate, setExistingUnenrollDate] = useState<string | undefined>(undefined);
	const [selectedStudent, setSelectedStudent] = useState<any>(null);
	const [selectedCourse, setSelectedCourse] = useState<string>("");
	const [enrollmentDate, setEnrollmentDate] = useState("");
	const [courseSearchTerm, setCourseSearchTerm] = useState("");

	// Signup link modal state
	const [signupLinkDialogOpen, setSignupLinkDialogOpen] = useState(false);
	const [signupLinkForm, setSignupLinkForm] = useState({
		studentFirstName: "",
		studentLastName: "",
		parentPhone: "",
		className: "",
	});
	const [signupLinkErrors, setSignupLinkErrors] = useState<Record<string, string>>({});

	const { data: students, isLoading: studentsLoading } = useQuery(
		getAllStudentsOptions(),
	);
	const { data: courses, isLoading: coursesLoading } = useQuery(
		getAllCoursesOptions(),
	);

	const enrollStudent = useMutation({
		...enrollStudentMutation(),
		onSuccess: () => {
			toast.success("Élève inscrit au cours avec succès");
			queryClient.invalidateQueries({ queryKey: getAllStudentsQueryKey() });
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setEnrollDialogOpen(false);
			setSelectedStudent(null);
			setSelectedCourse("");
			setCourseSearchTerm("");
			setEnrollmentDate("");
		},
		onError: () => {
			toast.error("Erreur lors de l'inscription");
		},
	});

	const unenrollStudent = useMutation({
		...unenrollStudentMutation(),
		onSuccess: () => {
			toast.success("Élève désinscrit du cours avec succès");
			queryClient.invalidateQueries({ queryKey: getAllStudentsQueryKey() });
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors de la désinscription");
		},
	});

	const cancelUnenrollMut = useMutation({
		...cancelUnenrollStudentMutation(),
		onSuccess: () => {
			toast.success("Désinscription annulée");
			queryClient.invalidateQueries({ queryKey: getAllStudentsQueryKey() });
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setUnenrollDialogOpen(false);
			setUnenrollStudentId("");
			setUnenrollStudentName("");
			setUnenrollCourseId("");
			setUnenrollCourseName("");
			setExistingUnenrollDate(undefined);
		},
		onError: () => {
			toast.error("Erreur lors de l'annulation de la désinscription");
		},
	});

	const sendSignupLink = useMutation({
		...createAndSendSignupLinkMutation(),
		onSuccess: () => {
			toast.success("Lien d'inscription envoyé par SMS avec succès");
			setSignupLinkDialogOpen(false);
			setSignupLinkForm({
				studentFirstName: "",
				studentLastName: "",
				parentPhone: "",
				className: "",
			});
			setSignupLinkErrors({});
		},
		onError: () => {
			toast.error("Erreur lors de l'envoi du lien d'inscription");
		},
	});

	const handleSendSignupLink = () => {
		const errors: Record<string, string> = {};
		if (!signupLinkForm.studentFirstName.trim()) {
			errors.studentFirstName = "Le prénom est obligatoire";
		}
		if (!signupLinkForm.studentLastName.trim()) {
			errors.studentLastName = "Le nom est obligatoire";
		}
		if (!signupLinkForm.parentPhone.trim()) {
			errors.parentPhone = "Le téléphone est obligatoire";
		}
		if (Object.keys(errors).length > 0) {
			setSignupLinkErrors(errors);
			return;
		}
		setSignupLinkErrors({});
		sendSignupLink.mutate({
			body: {
				studentFirstName: signupLinkForm.studentFirstName,
				studentLastName: signupLinkForm.studentLastName,
				parentPhone: signupLinkForm.parentPhone,
				className: signupLinkForm.className || undefined,
			},
		});
	};

	const handleEnroll = () => {
		if (!selectedStudent || !selectedCourse) return;

		enrollStudent.mutate({
			path: {
				courseId: selectedCourse,
				studentId: selectedStudent.id,
			},
			body: {
				enrollmentDate: enrollmentDate || new Date().toISOString().split("T")[0],
			},
		});
	};

	const handleOpenUnenrollDialog = (studentId: string, studentName: string, courseId: string, courseName: string, unrolledDate?: string) => {
		setUnenrollStudentId(studentId);
		setUnenrollStudentName(studentName);
		setUnenrollCourseId(courseId);
		setUnenrollCourseName(courseName);
		setExistingUnenrollDate(unrolledDate || undefined);
		setUnenrollDialogOpen(true);
	};

	const handleCancelUnenroll = () => {
		if (!unenrollStudentId || !unenrollCourseId) return;
		cancelUnenrollMut.mutate({
			path: {
				courseId: unenrollCourseId,
				studentId: unenrollStudentId,
			},
		});
	};

	const handleUnenrollConfirm = (unenrollDate: string) => {
		if (!unenrollStudentId || !unenrollCourseId) return;
		unenrollStudent.mutate({
			path: {
				courseId: unenrollCourseId,
				studentId: unenrollStudentId,
			},
			body: {
				unenrollDate,
			},
		});
		setUnenrollDialogOpen(false);
		setUnenrollStudentId("");
		setUnenrollStudentName("");
		setUnenrollCourseId("");
		setUnenrollCourseName("");
		setExistingUnenrollDate(undefined);
	};

	const dayOfWeekLabels: Record<string, string> = {
		MONDAY: "Lundi",
		TUESDAY: "Mardi",
		WEDNESDAY: "Mercredi",
		THURSDAY: "Jeudi",
		FRIDAY: "Vendredi",
		SATURDAY: "Samedi",
		SUNDAY: "Dimanche",
	};

	const columns = useMemo<ColumnDef<any, any>[]>(
		() => [
			{
				id: "class",
				accessorFn: (row) => row.className || "",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Classe" />
				),
				cell: ({ row }) => (
					<div className="text-sm">
						{row.original.className ? (
							<span>
								{row.original.className} ({getLevelLabel(row.original.level)})
							</span>
						) : (
							<span>{getLevelLabel(row.original.level)}</span>
						)}
					</div>
				),
				sortDescFirst: false,
				meta: {
					label: "Classe",
					textValue: (row: any) =>
						row.className ? `${row.className} (${getLevelLabel(row.level)})` : getLevelLabel(row.level),
				},
			},
			{
				id: "student",
				accessorFn: (row) => row.lastName + " " + row.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Élève" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<GraduationCap className="h-4 w-4 text-blue-600" />
						<div>
							<p className="font-medium">
								{row.original.firstName} {row.original.lastName}
							</p>
						</div>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					return fullName.includes(searchValue) || reverseName.includes(searchValue);
				},
				sortDescFirst: false,
				meta: { label: "Élève" },
			},
			{
				id: "parent",
				accessorFn: (row) => row.parent.lastName + " " + row.parent.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => (
					<div>
						<p className="text-sm font-medium">
							{row.original.parent.firstName} {row.original.parent.lastName}
						</p>
						<p className="text-xs text-gray-600">
							{row.original.parent.email}
						</p>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.parent.firstName} ${row.original.parent.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.parent.lastName} ${row.original.parent.firstName}`
					);
					const email = normalizeString(row.original.parent.email || "");
					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						email.includes(searchValue)
					);
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
			{
				id: "school",
				accessorFn: (row) => getSchoolLabel(row.school),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Établissement" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-1">
						<School className="h-3 w-3 text-gray-400" />
						<span className="text-sm">
							{getSchoolLabel(row.original.school)}
						</span>
					</div>
				),
				meta: { label: "Établissement" },
			},
			{
				id: "needs",
				accessorFn: (row) => {
					const needs = [];
					if (row.hasPap) needs.push("PAP");
					if (row.hasPps) needs.push("PPS");
					return needs.join(", ") || "-";
				},
				header: "Besoins",
				enableSorting: false,
				cell: ({ row }) => (
					<div className="flex gap-1">
						{row.original.hasPap && (
							<Badge variant="secondary" className="text-xs">
								PAP
							</Badge>
						)}
						{row.original.hasPps && (
							<Badge variant="secondary" className="text-xs">
								PPS
							</Badge>
						)}
						{!row.original.hasPap && !row.original.hasPps && (
							<span className="text-gray-400 text-sm">-</span>
						)}
					</div>
				),
				meta: { label: "Besoins" },
			},
			{
				id: "enrollments",
				accessorFn: (row) => {
					if (!row.enrollments || row.enrollments.length === 0) return "Aucun cours";
					return row.enrollments.map((e: any) => {
						const ts = getTimeSlot(e.course.timeSlot);
						return `${getLevelLabel(e.course.level)} ${getSubjectLabel(e.course.subject)} ${ts?.startTime || ""}`;
					}).join(", ");
				},
				header: "Cours inscrits",
				enableSorting: false,
				cell: ({ row }) => {
					const student = row.original;
					return student.enrollments && student.enrollments.length > 0 ? (
						<div className="space-y-1">
							{student.enrollments.slice(0, 2).map((enrolment: any) => {
								const timeSlot = getTimeSlot(enrolment.course.timeSlot);
								const teacherInitials = enrolment.course.teacherName
									? enrolment.course.teacherName.substring(0, 3).toUpperCase()
									: "";
								return (
									<div
										key={enrolment.id}
										className="flex items-center gap-2"
									>
										<Badge
											variant="outline"
											className={`text-xs ${enrolment.unrolledDate ? "border-orange-300 bg-orange-50 text-orange-700" : ""}`}
											title={enrolment.unrolledDate ? `Désinscription le ${new Date(enrolment.unrolledDate).toLocaleDateString("fr-FR")}` : undefined}
										>
											{getLevelLabel(enrolment.course.level)}{" "}
											{getSubjectLabel(enrolment.course.subject)}{" "}
											{timeSlot?.startTime}
											{teacherInitials && ` (${teacherInitials})`}
											{enrolment.unrolledDate && " \u23F3"}
										</Badge>
										<Button
											size="sm"
											variant="ghost"
											className="h-5 w-5 p-0"
											onClick={() =>
												handleOpenUnenrollDialog(
													student.id || "",
													`${student.firstName} ${student.lastName}`,
													enrolment.course.id || "",
													`${getSubjectLabel(enrolment.course.subject)} - ${getLevelLabel(enrolment.course.level)}`,
													enrolment.unrolledDate,
												)
											}
										>
											<UserMinus className={`h-3 w-3 ${enrolment.unrolledDate ? "text-orange-500" : ""}`} />
										</Button>
									</div>
								);
							})}
							{student.enrollments.length > 2 && (
								<p className="text-xs text-gray-500">
									+{student.enrollments.length - 2} autre(s)
								</p>
							)}
						</div>
					) : (
						<span className="text-gray-400 text-sm">
							Aucun cours
						</span>
					);
				},
				meta: { label: "Cours inscrits" },
			},
			{
				id: "actions",
				header: "Actions",
				enableSorting: false,
				cell: ({ row }) => {
					const student = row.original;
					return (
						<div className="flex items-center gap-2">
							<Link
								to="/admin/students/$id"
								params={{ id: student.id! }}
							>
								<Button
									size="sm"
									variant="outline"
									title="Voir l'enfant"
								>
									<Eye className="h-3 w-3" />
								</Button>
							</Link>
							<Button
								size="sm"
								variant="outline"
								onClick={() => {
									setSelectedStudent(student);
									setEnrollDialogOpen(true);
								}}
							>
								<UserPlus className="h-3 w-3 mr-1" />
								Inscrire
							</Button>
						</div>
					);
				},
				meta: { label: "Actions" },
			},
		],
		[],
	);

	if (studentsLoading || coursesLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement des élèves...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Gestion des élèves</h1>
					<p className="text-gray-600 mt-2">
						Gérez les inscriptions et les informations des élèves
					</p>
				</div>
				<Button
								variant="outline"
								onClick={() => setSignupLinkDialogOpen(true)}
							>
								<Send className="h-4 w-4 mr-2" />
								Lien d'inscription
							</Button>
			</div>

			<Card>
				<CardHeader>
					<div>
						<CardTitle>
							Liste des élèves ({(students || []).length})
						</CardTitle>
						<CardDescription>
							Tous les élèves inscrits dans le système
						</CardDescription>
					</div>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={students || []}
						emptyMessage="Aucun élève trouvé"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="eleves">
								<DataTableFilterText
									column={table.getColumn("student")!}
									placeholder="Rechercher un élève..."
								/>
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>

			{/* Dialog lien d'inscription par SMS */}
			<Dialog open={signupLinkDialogOpen} onOpenChange={setSignupLinkDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Générer un lien d'inscription</DialogTitle>
						<DialogDescription>
							Renseignez les informations de l'élève pour générer un lien
							d'inscription pré-rempli et l'envoyer par SMS au parent.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<Label htmlFor="signup-firstName">Prénom de l'élève *</Label>
								<Input
									id="signup-firstName"
									value={signupLinkForm.studentFirstName}
									onChange={(e) => {
										setSignupLinkForm({ ...signupLinkForm, studentFirstName: e.target.value });
										if (signupLinkErrors.studentFirstName) {
											setSignupLinkErrors({ ...signupLinkErrors, studentFirstName: "" });
										}
									}}
									placeholder="Prénom"
									className={signupLinkErrors.studentFirstName ? "border-red-500" : ""}
								/>
								{signupLinkErrors.studentFirstName && (
									<p className="text-sm text-red-600 mt-1">{signupLinkErrors.studentFirstName}</p>
								)}
							</div>
							<div>
								<Label htmlFor="signup-lastName">Nom de l'élève *</Label>
								<Input
									id="signup-lastName"
									value={signupLinkForm.studentLastName}
									onChange={(e) => {
										setSignupLinkForm({ ...signupLinkForm, studentLastName: e.target.value });
										if (signupLinkErrors.studentLastName) {
											setSignupLinkErrors({ ...signupLinkErrors, studentLastName: "" });
										}
									}}
									placeholder="Nom"
									className={signupLinkErrors.studentLastName ? "border-red-500" : ""}
								/>
								{signupLinkErrors.studentLastName && (
									<p className="text-sm text-red-600 mt-1">{signupLinkErrors.studentLastName}</p>
								)}
							</div>
						</div>
						<div>
							<Label htmlFor="signup-phone">Téléphone du parent *</Label>
							<Input
								id="signup-phone"
								type="tel"
								value={signupLinkForm.parentPhone}
								onChange={(e) => {
									setSignupLinkForm({ ...signupLinkForm, parentPhone: e.target.value });
									if (signupLinkErrors.parentPhone) {
										setSignupLinkErrors({ ...signupLinkErrors, parentPhone: "" });
									}
								}}
								placeholder="06 12 34 56 78"
								className={signupLinkErrors.parentPhone ? "border-red-500" : ""}
							/>
							{signupLinkErrors.parentPhone && (
								<p className="text-sm text-red-600 mt-1">{signupLinkErrors.parentPhone}</p>
							)}
						</div>
						<div>
							<Label htmlFor="signup-className">Classe (optionnel)</Label>
							<Input
								id="signup-className"
								value={signupLinkForm.className}
								onChange={(e) =>
									setSignupLinkForm({ ...signupLinkForm, className: e.target.value })
								}
								placeholder="Ex: 6ème A, CM2..."
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setSignupLinkDialogOpen(false);
								setSignupLinkForm({
									studentFirstName: "",
									studentLastName: "",
									parentPhone: "",
									className: "",
								});
								setSignupLinkErrors({});
							}}
						>
							Annuler
						</Button>
						<Button
							onClick={handleSendSignupLink}
							disabled={sendSignupLink.isPending}
						>
							{sendSignupLink.isPending ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Envoi en cours...
								</>
							) : (
								<>
									<Send className="h-4 w-4 mr-2" />
									Envoyer par SMS
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Dialog de désinscription */}
			<UnenrollDialog
				open={unenrollDialogOpen}
				onOpenChange={setUnenrollDialogOpen}
				studentName={unenrollStudentName}
				courseName={unenrollCourseName}
				isPending={unenrollStudent.isPending}
				isCancelPending={cancelUnenrollMut.isPending}
				existingUnenrollDate={existingUnenrollDate}
				onConfirm={handleUnenrollConfirm}
				onCancelUnenroll={existingUnenrollDate ? handleCancelUnenroll : undefined}
			/>

			{/* Dialog d'inscription */}
			<Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Inscrire l'élève à un cours</DialogTitle>
						<DialogDescription>
							Sélectionnez le cours pour {selectedStudent?.firstName}{" "}
							{selectedStudent?.lastName}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						{selectedStudent && (
							<div className="bg-gray-50 p-4 rounded-lg">
								<p className="font-medium">
									{selectedStudent.firstName} {selectedStudent.lastName}
								</p>
								<p className="text-sm text-gray-600">
									{selectedStudent.school}
								</p>
								{selectedStudent.enrollments?.length > 0 && (
									<div className="mt-2">
										<p className="text-xs text-gray-500 mb-1">
											Déjà inscrit à :
										</p>
										{selectedStudent.enrollments.map((e: any) => (
											<Badge
												key={e.id}
												variant="secondary"
												className="text-xs mr-1"
											>
												{getSubjectLabel(e.course.subject)} -{" "}
												{getLevelLabel(e.course.level)}
											</Badge>
										))}
									</div>
								)}
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="enrollment-date">Date d'inscription (optionnel)</Label>
							<Input
								id="enrollment-date"
								type="date"
								value={enrollmentDate}
								onChange={(e) => setEnrollmentDate(e.target.value)}
							/>
							<p className="text-xs text-gray-500">
								Par défaut : aujourd'hui
							</p>
						</div>

						<div className="space-y-2">
							<Label htmlFor="course-search">Rechercher un cours</Label>
							<Input
								id="course-search"
								placeholder="Rechercher par matière, niveau, professeur..."
								value={courseSearchTerm}
								onChange={(e) => setCourseSearchTerm(e.target.value)}
							/>
							<div className="border rounded-md max-h-[300px] overflow-y-auto">
								{courses
									?.filter(
										(course) =>
											!selectedStudent?.enrollments?.some(
												(e: any) => e.course.id === course.id,
											),
									)
									.filter((course) => {
										if (!courseSearchTerm) return true;
										const searchLower = courseSearchTerm.toLowerCase();
										const timeSlot = getTimeSlot(course.timeSlot);
										return (
											course.subject?.toLowerCase().includes(searchLower) ||
											course.level?.toLowerCase().includes(searchLower) ||
											course.teacherName?.toLowerCase().includes(searchLower) ||
											dayOfWeekLabels[timeSlot?.dayOfWeek || ""]
												?.toLowerCase()
												.includes(searchLower)
										);
									})
									.map((course) => {
										const timeSlot = getTimeSlot(course.timeSlot);
										const hasConflict = selectedStudent?.enrollments?.some(
											(e: any) => e.course.timeSlot === course.timeSlot,
										);
										const isSelected = selectedCourse === course.id;
										const teacherInitials = course.teacherName
											? course.teacherName.substring(0, 3).toUpperCase()
											: "";
										return (
											<div
												key={course.id}
												onClick={() => !hasConflict && setSelectedCourse(course.id || "")}
												className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
													hasConflict ? "opacity-50 cursor-not-allowed" : ""
												} ${isSelected ? "bg-blue-50 hover:bg-blue-100" : ""}`}
											>
												<div className="flex items-center justify-between">
													<div>
														<p className="font-medium">
															{getLevelLabel(course.level)} {getSubjectLabel(course.subject)}{" "}
															{timeSlot?.startTime}
															{teacherInitials && ` (${teacherInitials})`}
														</p>
														<p className="text-sm text-gray-600">
															{dayOfWeekLabels[timeSlot?.dayOfWeek || ""]} {timeSlot?.startTime} - {timeSlot?.endTime}
															{course.teacherName && ` - Prof: ${course.teacherName}`}
														</p>
														{hasConflict && (
															<p className="text-xs text-red-600 mt-1">Conflit d'horaire</p>
														)}
													</div>
													{isSelected && (
														<div className="h-4 w-4 bg-blue-600 rounded-full flex items-center justify-center">
															<div className="h-2 w-2 bg-white rounded-full"></div>
														</div>
													)}
												</div>
											</div>
										);
									})}
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setEnrollDialogOpen(false);
								setSelectedStudent(null);
								setSelectedCourse("");
								setCourseSearchTerm("");
								setEnrollmentDate("");
							}}
						>
							Annuler
						</Button>
						<Button
							onClick={handleEnroll}
							disabled={!selectedCourse || enrollStudent.isPending}
						>
							{enrollStudent.isPending ? "Inscription..." : "Inscrire l'élève"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
